import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, X, Trash, Pencil, HandHeart, Prohibit } from '@phosphor-icons/react';

const LiveClassesView = ({
  liveClasses, sessionTypes, liveClassCategories, sessionTypeCategoryFilter,
  setSessionTypeCategoryFilter,
  editingClass, editClassForm,
  showAddSessionType, newSessionTypeName,
  setEditingClass, setEditClassForm, setShowAddSessionType, setNewSessionTypeName,
  onToggleBlock, onDeleteClass, onUpdateClass,
  onAddSessionType, onToggleSessionType, onDeleteSessionType,
  onToggleLiveClassCategory,
}) => {
  const total = liveClasses.length;
  const liveCount = liveClasses.filter(c => c.status === 'live').length;
  const blockedCount = liveClasses.filter(c => c.blocked).length;
  const totalParticipants = liveClasses.reduce((s, c) => s + (c.booked_count || 0), 0);

  return (
    <div className="space-y-6">
      <p className="text-[#6b7a99] text-sm">Manage live yoga / wellness classes & session types across the platform.</p>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4">
          <p className="text-[11px] text-[#6b7a99] uppercase tracking-wider">Total Classes</p>
          <p className="text-2xl font-bold text-white mt-1">{total}</p>
        </div>
        <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4">
          <p className="text-[11px] text-[#6b7a99] uppercase tracking-wider">Live Now</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{liveCount}</p>
        </div>
        <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4">
          <p className="text-[11px] text-[#6b7a99] uppercase tracking-wider">Blocked</p>
          <p className="text-2xl font-bold text-amber-400 mt-1">{blockedCount}</p>
        </div>
        <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-4">
          <p className="text-[11px] text-[#6b7a99] uppercase tracking-wider">Total Bookings</p>
          <p className="text-2xl font-bold text-pink-400 mt-1">{totalParticipants}</p>
        </div>
      </div>

      {/* Live Sessions Per Category */}
      <div className="bg-[#161b26] border border-[#252d3d] rounded-2xl p-5" data-testid="live-class-categories-panel">
        <div className="mb-3">
          <h3 className="text-white font-semibold text-sm">Categories with Live Sessions</h3>
          <p className="text-[#6b7a99] text-xs mt-0.5">Toggle which categories allow sellers to host Live Sessions. Sellers in enabled categories see the "Create Class" button on their dashboard.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {(liveClassCategories || []).map(cat => (
            <label key={cat.category}
              className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${cat.live_classes_enabled ? 'bg-pink-500/10 border-pink-500/40 text-pink-300' : 'bg-[#0f1219] border-[#252d3d] text-[#a5b4d0] hover:border-[#3a455c]'}`}
              data-testid={`lc-cat-${cat.category}`}>
              <span className="text-xs font-medium truncate">{cat.label}</span>
              <button onClick={(e) => { e.preventDefault(); onToggleLiveClassCategory(cat.category); }}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${cat.live_classes_enabled ? 'bg-pink-500' : 'bg-[#252d3d]'}`}
                data-testid={`lc-cat-toggle-${cat.category}`}>
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${cat.live_classes_enabled ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
            </label>
          ))}
        </div>
      </div>

      {/* Session Types Management */}
      <div className="bg-[#161b26] border border-[#252d3d] rounded-2xl p-5" data-testid="session-types-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <h3 className="text-white font-semibold text-sm">Session Types</h3>
            <p className="text-[#6b7a99] text-xs mt-0.5">Configure session styles per category. Sellers see these when creating classes.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sessionTypeCategoryFilter}
              onChange={e => setSessionTypeCategoryFilter(e.target.value)}
              className="bg-[#0f1219] border border-[#252d3d] text-white text-xs rounded-lg h-8 px-2"
              data-testid="session-type-category-select">
              {(liveClassCategories || []).map(c => (
                <option key={c.category} value={c.category}>{c.label}</option>
              ))}
            </select>
            <Button size="sm" onClick={() => setShowAddSessionType(v => !v)}
              className="bg-pink-500 hover:bg-pink-600 text-white rounded-lg text-xs h-8" data-testid="add-session-type-btn">
              <Plus size={14} className="mr-1" /> Add Type
            </Button>
          </div>
        </div>
        {showAddSessionType && (
          <div className="flex gap-2 mb-3">
            <Input value={newSessionTypeName} onChange={e => setNewSessionTypeName(e.target.value)}
              placeholder={`New session type for ${(liveClassCategories || []).find(c => c.category === sessionTypeCategoryFilter)?.label || sessionTypeCategoryFilter}`}
              className="bg-[#0f1219] border-[#252d3d] text-white h-9 text-sm rounded-lg flex-1"
              data-testid="new-session-type-input" />
            <Button size="sm" onClick={onAddSessionType} className="bg-emerald-500 hover:bg-emerald-600 rounded-lg h-9 text-xs" data-testid="save-session-type-btn">Save</Button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {sessionTypes.length === 0 ? (
            <span className="text-[#6b7a99] text-xs">No session types defined for this category yet. Click "Add Type" to create one.</span>
          ) : sessionTypes.map(st => (
            <div key={st.type_id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${st.active ? 'bg-pink-500/10 border-pink-500/30 text-pink-300' : 'bg-[#0f1219] border-[#252d3d] text-[#6b7a99]'}`}
              data-testid={`session-type-${st.type_id}`}>
              <span className="font-semibold">{st.name}</span>
              <button onClick={() => onToggleSessionType(st.type_id, !st.active)}
                className="text-[10px] uppercase tracking-wider hover:text-white" data-testid={`toggle-session-type-${st.type_id}`}>
                {st.active ? 'On' : 'Off'}
              </button>
              <button onClick={() => onDeleteSessionType(st.type_id)} className="text-red-400 hover:text-red-300" data-testid={`delete-session-type-${st.type_id}`}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Classes Table */}
      <div className="bg-[#161b26] rounded-2xl border border-[#252d3d] overflow-hidden">
        <div className="px-5 py-3 border-b border-[#252d3d] flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm">All Live Sessions</h3>
        </div>
        {total === 0 ? (
          <div className="p-12 text-center">
            <HandHeart size={36} className="text-[#252d3d] mx-auto mb-2" />
            <p className="text-[#6b7a99] text-sm">No live sessiones created yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead><tr className="border-b border-[#252d3d]">
              <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Class</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Host</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Date / Time</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Booked</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Visible In</th>
              <th className="text-left py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Status</th>
              <th className="text-right py-3 px-4 text-[11px] font-semibold text-[#6b7a99] uppercase tracking-wider">Actions</th>
            </tr></thead>
            <tbody>{liveClasses.map(c => (
              <React.Fragment key={c.class_id}>
                <tr className="border-b border-[#252d3d]/50 hover:bg-[#1e2536]/50" data-testid={`admin-class-row-${c.class_id}`}>
                  <td className="py-2.5 px-4">
                    <div className="text-white text-sm font-medium">{c.title}</div>
                    <div className="text-[#6b7a99] text-[11px]">{c.session_type}</div>
                  </td>
                  <td className="py-2.5 px-4 text-[#a5b4d0] text-sm">{c.seller_name || '-'}</td>
                  <td className="py-2.5 px-4 text-[#a5b4d0] text-xs">{c.date} <br /><span className="text-[#6b7a99]">{c.start_time} - {c.end_time}</span></td>
                  <td className="py-2.5 px-4 text-white text-sm font-medium">{c.booked_count}/{c.max_participants}</td>
                  <td className="py-2.5 px-4 text-[#a5b4d0] text-xs">
                    {c.country ? (
                      <span title={c.country}>{c.country}</span>
                    ) : (!c.visible_countries || c.visible_countries.length === 0) ? (
                      <span className="text-[#6b7a99]">🌐 Global</span>
                    ) : (
                      <span title={c.visible_countries.join(', ')}>{c.visible_countries.slice(0, 3).join(' • ')}{c.visible_countries.length > 3 ? ` +${c.visible_countries.length - 3}` : ''}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="flex flex-col gap-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold inline-flex w-fit ${c.status === 'live' ? 'bg-red-500/20 text-red-300' : c.status === 'completed' ? 'bg-slate-500/20 text-slate-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                        {(c.status || 'upcoming').toUpperCase()}
                      </span>
                      {c.blocked && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-500/20 text-amber-300 inline-flex items-center gap-1 w-fit">
                          <Prohibit size={10} /> BLOCKED
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => { setEditingClass(c.class_id); setEditClassForm({ title: c.title, max_participants: c.max_participants, price: c.price, status: c.status, country: c.country || '', visible_countries: c.visible_countries || [] }); }}
                        className="p-1.5 text-[#a5b4d0] hover:text-white rounded-lg hover:bg-[#1e2536]" title="Edit" data-testid={`edit-class-${c.class_id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => onToggleBlock(c.class_id, !c.blocked)}
                        className={`p-1.5 rounded-lg hover:bg-[#1e2536] ${c.blocked ? 'text-emerald-400 hover:text-emerald-300' : 'text-amber-400 hover:text-amber-300'}`}
                        title={c.blocked ? 'Unblock' : 'Block'} data-testid={`block-class-${c.class_id}`}>
                        <Prohibit size={14} />
                      </button>
                      <button onClick={() => onDeleteClass(c.class_id)}
                        className="p-1.5 text-red-400 hover:text-red-300 rounded-lg hover:bg-[#1e2536]" title="Delete" data-testid={`delete-class-${c.class_id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                {editingClass === c.class_id && (
                  <tr className="bg-[#0f1219]" data-testid={`edit-class-row-${c.class_id}`}>
                    <td colSpan={7} className="px-4 py-3">
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                        <div>
                          <label className="text-[10px] text-[#6b7a99] uppercase">Title</label>
                          <Input value={editClassForm.title || ''} onChange={e => setEditClassForm(f => ({ ...f, title: e.target.value }))}
                            className="bg-[#161b26] border-[#252d3d] text-white h-9 text-sm rounded-lg" data-testid="edit-class-title" />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#6b7a99] uppercase">Max Participants</label>
                          <Input type="number" value={editClassForm.max_participants || ''} onChange={e => setEditClassForm(f => ({ ...f, max_participants: e.target.value }))}
                            className="bg-[#161b26] border-[#252d3d] text-white h-9 text-sm rounded-lg" data-testid="edit-class-max" />
                        </div>
                        <div>
                          <label className="text-[10px] text-[#6b7a99] uppercase">Price</label>
                          <Input type="number" value={editClassForm.price || 0} onChange={e => setEditClassForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                            className="bg-[#161b26] border-[#252d3d] text-white h-9 text-sm rounded-lg" data-testid="edit-class-price" />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => onUpdateClass(c.class_id)} className="bg-emerald-500 hover:bg-emerald-600 rounded-lg h-9 text-xs" data-testid="save-edit-class">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingClass(null); setEditClassForm({}); }} className="bg-transparent border-[#252d3d] text-[#a5b4d0] rounded-lg h-9 text-xs">Cancel</Button>
                        </div>
                        <div className="sm:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-[#6b7a99] uppercase">Country (Timezone) — single ISO code, leave empty for Global</label>
                            <Input value={editClassForm.country || ''}
                              onChange={e => setEditClassForm(f => ({ ...f, country: e.target.value.toUpperCase() }))}
                              placeholder="e.g. IN (empty = Global)"
                              className="bg-[#161b26] border-[#252d3d] text-white h-9 text-sm rounded-lg" data-testid="edit-class-country" />
                          </div>
                          <div>
                            <label className="text-[10px] text-[#6b7a99] uppercase">Also Visible In (legacy comma-separated, optional)</label>
                            <Input value={(editClassForm.visible_countries || []).join(', ')}
                              onChange={e => setEditClassForm(f => ({ ...f, visible_countries: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) }))}
                              placeholder="Empty = none"
                              className="bg-[#161b26] border-[#252d3d] text-white h-9 text-sm rounded-lg" data-testid="edit-class-countries" />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LiveClassesView;
